import React from 'react';
import { QuestionRenderer, type QuestionAnswerValue } from '@/components/assessment-engine/QuestionRenderer';
import {
  groupDemographicQuestions,
  isDemographicsQuestionList,
} from '@/components/assessment-engine/demographicFieldLayout';
import type { AeEvaluatedQuestion } from '@/types/assessmentEngine';

interface DemographicQuestionsGridProps {
  questions: AeEvaluatedQuestion[];
  answersByQuestionId: Record<string, QuestionAnswerValue>;
  onChange: (questionId: string, value: QuestionAnswerValue) => void;
}

/**
 * Compact About you layout: contact row, org/role row, then remaining fields.
 * lg: up to 3 columns; md: 2; sm: 1.
 */
export const DemographicQuestionsGrid: React.FC<DemographicQuestionsGridProps> = ({
  questions,
  answersByQuestionId,
  onChange,
}) => {
  if (!isDemographicsQuestionList(questions)) {
    return (
      <div className="space-y-3">
        {questions.map((q) => (
          <QuestionRenderer
            key={q.id}
            question={q}
            compact
            value={answersByQuestionId[q.id] ?? {}}
            onChange={(val) => onChange(q.id, val)}
          />
        ))}
      </div>
    );
  }

  const rows = groupDemographicQuestions(questions);

  return (
    <div className="space-y-2.5">
      {rows.map((row, rowIndex) => (
        <div
          key={`demo-row-${rowIndex}`}
          className={`grid grid-cols-1 gap-2.5 ${
            row.length === 1
              ? 'md:grid-cols-1'
              : row.length === 2
                ? 'md:grid-cols-2'
                : 'md:grid-cols-2 lg:grid-cols-3'
          }`}
        >
          {row.map((q) => (
            <div key={q.id} className="min-w-0">
              <QuestionRenderer
                question={q}
                compact
                value={answersByQuestionId[q.id] ?? {}}
                onChange={(val) => onChange(q.id, val)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
